import { COMPANY_ID } from '@constants/auth.constant';
import { COMPANY_LIST } from '@constants/internal-route.constant';
import { useAuth } from '@context/Auth';
import { IAppRoutes } from '@interface/common.interface';
import ChooseCompany from '@modules/auth/ChooseCompany';
import { setWindowTitle } from '@services/helper/common.helper';
import { usePermissions } from '@services/helper/permission.helper';
import { LocalStorageService } from '@services/utils/localStorage.service';
import { TopProgressCom } from '@services/utils/topProgress.service';
import AuthLayout from 'layouts/AuthLayout';
import { MasterLayout } from 'layouts/MasterLayout';
import { Error404 } from 'pages/errors/components/Error404';
import { Unauthorized401 } from 'pages/errors/components/Unauthorized401';
import { Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppRouteList } from './routes/app.routes';

/**
 * May the user open a route? No routeCode means it is open (usePermissions().canSee); a route with
 * an `action` (add = 'create', edit = 'update') also needs that flag on its code.
 */
type Allowed = (route: IAppRoutes) => boolean;

/**
 * Can this person reach ANYTHING at or below this route?
 *
 * A container route (Inventory, Procurement, Reports…) has no screen of its own -- it exists to
 * render an <Outlet/> for its children. It was gated on its own code, so a role holding every child
 * of Inventory but not the INVENTORY group row got Unauthorized401 on the container, which killed
 * every child route beneath it. Gating a container on itself alone makes the group row a silent
 * prerequisite for screens the user has explicitly been granted.
 *
 * Nothing is widened: each child route is still checked against its own code below.
 */
const reachable = (route: IAppRoutes, allowed: Allowed): boolean =>
  allowed(route) || (route?.childrens || []).some((c: IAppRoutes) => reachable(c, allowed));

const routeList = (routes: IAppRoutes[], allowed: Allowed) => {
  return routes.map((route: IAppRoutes, index: number) => {
    const isUnauthorized = !allowed(route);

    if (route.childrens && route.childrens.length) {
      // A container that owns a screen still gates on its own code; one that is purely a parent
      // gates on whether anything inside it is reachable.
      const blocked = route.element ? isUnauthorized : !reachable(route, allowed);
      return (
        <Route
          path={route.link}
          element={
            route.element && !isUnauthorized ? (
              <Suspense fallback={<TopProgressCom />}>
                <route.element />
              </Suspense>
            ) : blocked ? (
              <Unauthorized401 />
            ) : (
              <>
                <Outlet />
              </>
            )
          }
          key={index}
        >
          {routeList(route.childrens, allowed)}
        </Route>
      );
    }

    return (
      <Route
        path={route.link}
        element={
          route.redirect ? (
            <Navigate to={route.redirect} replace />
          ) : isUnauthorized ? (
            <Unauthorized401 />
          ) : (
            <Suspense fallback={<TopProgressCom />}>
              <route.element />
            </Suspense>
          )
        }
        key={index}
      />
    );
  });
};

const PrivateRoutes = () => {
  const { userInfo } = useAuth();
  const { canSee, can } = usePermissions();
  const allowed: Allowed = (route) =>
    canSee(route?.routeCode) && (!route?.action || !route?.routeCode || can(route.routeCode, route.action));
  setWindowTitle();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const cid: string = LocalStorageService.get(COMPANY_ID);
    if (location.pathname !== COMPANY_LIST && !cid) navigate(COMPANY_LIST);
  }, [location, navigate]);

  /**
   * The cached profile can legitimately be missing while a valid token is present.
   *
   * AuthProvider writes the token at sign-in but only writes USER_INFO once verify-token answers,
   * and `isAuthenticated` is derived from the TOKEN alone -- so a reload inside that window renders
   * this component with no `userInfo` at all. Destructuring it unguarded turned that gap into a
   * render-time TypeError, which aborts the whole React root: AuthProvider's bootstrap effect then
   * never ran, the cache was never repaired, and every subsequent reload failed identically.
   *
   * Holding the splash instead lets the provider re-fetch and this render on the next pass. It is
   * deliberately NOT a fallback to an empty feature list -- that would swap the crash for a console
   * where every gated route answers Unauthorized401, which is harder to diagnose, not easier.
   */
  if (!userInfo) return <TopProgressCom />;

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path={COMPANY_LIST} element={<ChooseCompany />} />
      </Route>
      <Route element={<MasterLayout />}>
        {routeList(AppRouteList, allowed)}
        <Route path='*' element={<Error404 />} />
      </Route>
    </Routes>
  );
};

export { PrivateRoutes };
